import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../api/axios';
import { useAuth } from './AuthContext';

const ModulesContext = createContext(null);

// Default shape so consumers don't crash before the first fetch resolves.
const EMPTY = {
    modules: [],
    byName: {},
    loading: true,
    reload: () => {},
    consume: () => {},
};

/**
 * Provides per-org module access state (trial / paid / view_only / missing)
 * so feature pages can render gating UI without each duplicating the call to
 * /api/modules/status.
 *
 * Update path:
 *   - On mount + on user change → fetch /modules/status
 *   - After a known-consuming action the page can call `consume(name)` to
 *     optimistically decrement; the next reload will reconcile with the server
 */
export const ModulesProvider = ({ children }) => {
    const { user } = useAuth();
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        if (!user) {
            setModules([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const res = await api.get('/modules/status');
            setModules(Array.isArray(res.data?.modules) ? res.data.modules : []);
        } catch (err) {
            setModules([]);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => { load(); }, [load]);

    const consume = useCallback((moduleName, deltaRemaining) => {
        setModules(prev => prev.map(m => {
            if (m.moduleName !== moduleName) return m;
            if (typeof deltaRemaining === 'number') {
                return { ...m, trialUsesRemaining: Math.max(0, deltaRemaining) };
            }
            if (m.accessLevel !== 'trial') return m;
            const next = Math.max(0, (m.trialUsesRemaining || 0) - 1);
            return {
                ...m,
                trialUsesRemaining: next,
                accessLevel: next === 0 ? 'view_only' : 'trial',
            };
        }));
    }, []);

    const byName = modules.reduce((acc, m) => { acc[m.moduleName] = m; return acc; }, {});

    return (
        <ModulesContext.Provider value={{ modules, byName, loading, reload: load, consume }}>
            {children}
        </ModulesContext.Provider>
    );
};

export const useModules = () => {
    const ctx = useContext(ModulesContext);
    return ctx || EMPTY;
};

export const useModule = (moduleName) => {
    const { byName, loading, reload, consume } = useModules();
    return {
        state: byName[moduleName] || null,
        loading,
        reload,
        consume: () => consume(moduleName),
    };
};
